import { audiences, C, channels, components, factories, formats, mechanics, testerPools, themes, type Unlockable, studies } from "@bd/content";
import { yearOf } from "./calendar";
import type { Company, GameState } from "./state";
import { log, rng } from "./util";
import { mechThemeFit } from "./design";
import { perkOptions, eraCostMult } from "./perks";
import { yearShift } from "./ngplus";

/** Prefixes for unlock ids: m: mechanic, t: theme, a: audience, f: format, c: component, ch: channel, fa: factory, p: tester pool. */
export const KINDS = {
  m: mechanics,
  t: themes,
  a: audiences,
  f: formats,
  c: components,
  ch: channels,
  fa: factories,
  p: testerPools,
} as const;
export type UnlockKind = keyof typeof KINDS;

export function unlockId(kind: UnlockKind, id: string): string {
  return `${kind}:${id}`;
}

type Cached = string[] & { __set?: { n: number; set: Set<string> } };

/** Fast membership test on co.unlocked: a Set kept on the array itself (non-enumerable, so saves never see it). */
export function isUnlocked(co: Company, id: string): boolean {
  const list = co.unlocked as Cached;
  let c = list.__set;
  if (!c || c.n !== list.length) {
    c = { n: list.length, set: new Set(list) };
    Object.defineProperty(list, "__set", { value: c, enumerable: false, writable: true, configurable: true });
  }
  return c.set.has(id);
}

export function unlock(co: Company, id: string): boolean {
  if (isUnlocked(co, id)) return false;
  co.unlocked.push(id);
  return true;
}

export function lookup(id: string): (Unlockable & { research?: number }) | undefined {
  const [kind, key] = splitId(id);
  const list = KINDS[kind as UnlockKind] as unknown as (Unlockable & { id: string })[] | undefined;
  return list?.find((x) => x.id === key);
}

export function splitId(id: string): [string, string] {
  const i = id.indexOf(":");
  return [id.slice(0, i), id.slice(i + 1)];
}

/** Everything a brand-new company starts with. */
export function startingUnlocks(): string[] {
  const out: string[] = [];
  for (const m of mechanics) if (m.start) out.push(unlockId("m", m.id));
  for (const t of themes) if (t.start) out.push(unlockId("t", t.id));
  for (const a of audiences) if (a.start) out.push(unlockId("a", a.id));
  for (const f of formats) if (f.start) out.push(unlockId("f", f.id));
  for (const c of components) if (c.year <= 1965 && !c.research) out.push(unlockId("c", c.id));
  for (const ch of channels) if (ch.start) out.push(unlockId("ch", ch.id));
  for (const fa of factories) if (fa.start) out.push(unlockId("fa", fa.id));
  out.push(unlockId("p", "friends"));
  return out;
}

export type Branch = "mechanics" | "components" | "business" | "studio";
export const BRANCH_OF: Record<UnlockKind | "pk", Branch> = { m: "mechanics", t: "mechanics", a: "business", f: "components", c: "components", ch: "business", fa: "business", p: "studio", pk: "studio" };

export interface ResearchOption {
  id: string; // unlock id, "pk:<perk>", or "st:<study>" (repeatable)
  name: string;
  kind: UnlockKind | "pk" | "st";
  branch: Branch;
  cost: number;
  ahead: boolean; // researched before its year: costs triple
}

/** What the company could research right now. Content from up to 5 years ahead is available at a steep cost. */
/** How long an item takes: bigger ideas take longer, and early ones half as long again. */
export function researchWeeks(o: Pick<ResearchOption, "cost" | "ahead">): number {
  return Math.max(3, Math.min(26, Math.round((3 + Math.sqrt(o.cost) * 1.6) * (o.ahead ? 1.5 : 1))));
}

/** A week of research: designers and developers speed it up a little; finished items unlock. */
export function researchWeek(state: GameState, co: Company): void {
  // once a month, points past the cap turn into pace (or stop growing)
  if (state.week % 4 === 0) {
    const cap = researchCap(co, state.week);
    if (co.research > cap) {
      const extra = co.research - cap;
      co.research = cap;
      if (co.researching?.length) for (const r of co.researching) r.left -= Math.min(4, extra / 4 / co.researching.length);
    }
  }
  if (!co.researching?.length) return;
  const thinkers = co.staff.filter((s) => s.role === "designer" || s.role === "developer" || s.founder).length;
  const pace = 1 + Math.min(0.5, thinkers * 0.04);
  for (const r of co.researching) r.left -= pace;
  for (const r of co.researching.filter((x) => x.left <= 0)) finishResearch(state, co, r.id);
  co.researching = co.researching.filter((x) => x.left > 0);
}

function finishResearch(state: GameState, co: Company, id: string): void {
  if (id.startsWith("st:")) return finishStudy(state, co, id.slice(3));
  if (id.startsWith("pk:")) {
    const perk = C.perk[id.slice(3)];
    if (!perk || co.researched.includes(perk.id)) return;
    co.researched.push(perk.id);
    if (perk.fx.rep) co.reputation = Math.min(100, co.reputation + perk.fx.rep);
    log(state, "research", `${co.name} finishes researching ${perk.name}.`, co.id);
    return;
  }
  unlock(co, id);
  // researching a mechanic or theme reveals how each audience feels about it
  const [kind, key] = id.split(":");
  if (kind === "m" && C.mechanic[key]) for (const [a, v] of Object.entries(C.mechanic[key].audience)) co.notebook[`ma:${key}:${a}`] = v;
  if (kind === "t" && C.theme[key]) for (const [a, v] of Object.entries(C.theme[key].audience)) co.notebook[`ta:${key}:${a}`] = v;
  log(state, "research", `${co.name} finishes researching ${lookup(id)?.name ?? id}.`, co.id);
}

export function researchOptions(co: Company, week: number): ResearchOption[] {
  const year = yearOf(week);
  const out: ResearchOption[] = [];
  for (const kind of Object.keys(KINDS) as UnlockKind[]) {
    for (const item of KINDS[kind] as unknown as (Unlockable & { id: string; needsRoom?: string })[]) {
      const id = unlockId(kind, item.id);
      if (isUnlocked(co, id)) continue;
      if ((co.researching ?? []).some((r) => r.id === id)) continue;
      if (item.hidden) continue; // hidden mechanics unlock through unusual play
      if (item.research === undefined && !(kind === "c" && item.year > 1965)) continue;
      if (kind === "p" && item.needsRoom) continue; // pools that come with a room
      if (kind === "fa" && item.needsRoom) continue;
      const itemYear = item.year + yearShift(co, id);
      // dearer each era
      const base = Math.round((item.research ?? 4) * eraCostMult(Math.max(1965, item.year)));
      if (year >= itemYear) out.push({ id, name: item.name, kind, branch: BRANCH_OF[kind], cost: base, ahead: false });
      else if (year >= itemYear - 5) out.push({ id, name: item.name, kind, branch: BRANCH_OF[kind], cost: base * 3, ahead: true });
    }
  }
  for (const p of perkOptions(co, week)) out.push({ ...p, kind: "pk", branch: p.branch as Branch });
  for (const st of studyOptions(co, week)) out.push(st);
  return out.sort((a, b) => a.cost - b.cost);
}

/** The mechanic you've built most games on whose engine still has room to improve: the one a house rules engine works on. */
export function houseMechanic(co: Company): string | null {
  const count: Record<string, number> = {};
  for (const p of co.products) if (p.launched && p.kind === "base") count[p.design.mechanic] = (count[p.design.mechanic] ?? 0) + 1;
  const best = Object.entries(count).filter(([m]) => (co.mastery[m] ?? 0) < 0.95).sort((a, b) => b[1] - a[1])[0];
  return best ? best[0] : null;
}

/** Untried pairings of the mechanics and themes you have: what a market study can test. */
function untriedFits(co: Company): [string, string][] {
  const ms = co.unlocked.filter((u) => u.startsWith("m:")).map((u) => u.slice(2));
  const ts = co.unlocked.filter((u) => u.startsWith("t:")).map((u) => u.slice(2));
  const out: [string, string][] = [];
  for (const m of ms) for (const t of ts) if (co.notebook[`mt:${m}:${t}`] === undefined) out.push([m, t]);
  return out;
}

// whether a study still has something to find, checked once a quarter per company (it's asked every week);
// kept per company object, so two games in one process never share an answer
let leftCache = new WeakMap<Company, Map<string, boolean>>();
/** Forget what's left to find (every week, and every online quarter: see resetCaches). */
export function resetUnlockCaches(): void {
  leftCache = new WeakMap();
}
function leftToFind(co: Company, week: number, kind: "fits" | "audiences"): boolean {
  let m = leftCache.get(co);
  if (!m) leftCache.set(co, (m = new Map()));
  const key = `${kind}|${Math.floor(week / 13)}|${co.unlocked.length}`;
  let v = m.get(key);
  if (v === undefined) {
    if (m.size > 64) m.clear();
    v = kind === "fits" ? anyUntriedFit(co) : anyUnknownAudience(co);
    m.set(key, v);
  }
  return v;
}

/** Is there anything left for a market study or survey to find? (stops at the first one) */
function anyUntriedFit(co: Company): boolean {
  const ts = co.unlocked.filter((u) => u.startsWith("t:"));
  for (const u of co.unlocked) {
    if (!u.startsWith("m:")) continue;
    const m = u.slice(2);
    for (const t of ts) if (co.notebook[`mt:${m}:${t.slice(2)}`] === undefined) return true;
  }
  return false;
}

function anyUnknownAudience(co: Company): boolean {
  for (const u of co.unlocked) {
    const [k, id] = u.split(":");
    if (k === "m" && C.mechanic[id]) for (const a of Object.keys(C.mechanic[id].audience)) if (co.notebook[`ma:${id}:${a}`] === undefined) return true;
    if (k === "t" && C.theme[id]) for (const a of Object.keys(C.theme[id].audience)) if (co.notebook[`ta:${id}:${a}`] === undefined) return true;
  }
  return false;
}

function unknownAudiences(co: Company): string[] {
  const out: string[] = [];
  for (const u of co.unlocked) {
    const [k, id] = u.split(":");
    if (k === "m" && C.mechanic[id]) for (const a of Object.keys(C.mechanic[id].audience)) if (co.notebook[`ma:${id}:${a}`] === undefined) out.push(`ma:${id}:${a}`);
    if (k === "t" && C.theme[id]) for (const a of Object.keys(C.theme[id].audience)) if (co.notebook[`ta:${id}:${a}`] === undefined) out.push(`ta:${id}:${a}`);
  }
  return out;
}

/** Repeatable research, always on offer once you've released a game (while it still has something to find). */
export function studyOptions(co: Company, week: number): ResearchOption[] {
  if (co.stats.released < 1) return [];
  const out: ResearchOption[] = [];
  const era = eraCostMult(yearOf(week));
  for (const st of studies) {
    const id = `st:${st.id}`;
    // a house engine levels one step at a time; studies can run side by side
    if (st.effect === "mastery" && (co.researching ?? []).some((r) => r.id === id)) continue;
    let name = st.name;
    let cost = st.cost * era;
    if (st.effect === "fits" && !leftToFind(co, week, "fits")) continue;
    if (st.effect === "audiences" && !leftToFind(co, week, "audiences")) continue;
    if (st.effect === "mastery") {
      const m = houseMechanic(co);
      if (!m) continue;
      const level = co.houseLevels?.[m] ?? 0;
      name = `${st.name}: ${C.mechanic[m].name} (level ${level + 1})`;
      cost *= 1 + 0.35 * level;
    }
    out.push({ id, name, kind: "st", branch: st.branch, cost: Math.round(cost), ahead: false });
  }
  return out;
}

/** What a study says it does, filled in for this company. */
export function studyDesc(co: Company, id: string): string {
  const st = studies.find((x) => `st:${x.id}` === id);
  if (!st) return "";
  const m = houseMechanic(co);
  return st.desc.replace("{n}", String(st.reveals)).replace("{mechanic}", m ? C.mechanic[m].name : "your main mechanic");
}

/**
 * Banked points can't pile up for ever: past about two items' worth, the overflow speeds up whatever is being
 * researched, and with nothing running, it simply stops growing.
 */
export function researchCap(co: Company, week: number): number {
  const opts = researchOptions(co, week);
  const dear = opts.length ? Math.max(...opts.slice(-3).map((o) => o.cost)) : 20;
  return Math.max(20, Math.round(dear * 2));
}

function finishStudy(state: GameState, co: Company, sid: string): void {
  const st = studies.find((x) => x.id === sid);
  if (!st) return;
  const r = rng(state, `study:${co.id}`);
  if (st.effect === "fits") {
    const pool = untriedFits(co);
    const found: string[] = [];
    for (let i = 0; i < st.reveals && pool.length; i++) {
      const [m, t] = pool.splice(r.int(0, pool.length - 1), 1)[0];
      const fit = mechThemeFit(m, t);
      co.notebook[`mt:${m}:${t}`] = fit;
      found.push(`${C.mechanic[m].name} × ${C.theme[t].name} ${fit >= 0.6 ? "fits well" : fit >= 0.3 ? "is fine" : "falls flat"}`);
    }
    log(state, "research", found.length ? `Market study done: ${found.join("; ")}.` : "Market study done: every pairing you can make is already in your notebook.", co.id);
  } else if (st.effect === "audiences") {
    const pool = unknownAudiences(co);
    let n = 0;
    for (let i = 0; i < st.reveals && pool.length; i++) {
      const key = pool.splice(r.int(0, pool.length - 1), 1)[0];
      const [k, id, a] = key.split(":");
      co.notebook[key] = k === "ma" ? C.mechanic[id].audience[a as never] : C.theme[id].audience[a as never];
      n++;
    }
    log(state, "research", `Audience survey done: ${n} new notes on who likes what.`, co.id);
  } else {
    const m = houseMechanic(co);
    if (!m) return;
    co.mastery[m] = Math.min(1, (co.mastery[m] ?? 0) + 0.08);
    co.houseLevels = { ...(co.houseLevels ?? {}), [m]: (co.houseLevels?.[m] ?? 0) + 1 };
    log(state, "research", `Your ${C.mechanic[m].name} rules engine reaches level ${co.houseLevels[m]}: games built on it play better.`, co.id);
  }
}
